import { useEffect, ReactElement, useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { Navigate, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useApolloClient, gql } from '@apollo/client'
import styled from 'styled-components'
import { isEmpty } from 'lodash'

// @ts-expect-error TS(2307) FIXME Remove this comment to see the full error message
import logoKolors from 'assets/images/logo_kolors_azul_horizontal'
import Card from '@10xLabs/react-web/components/Card'
import { resolvers as loginResolvers } from 'resolvers/login'
import LoginForm from 'features/Login/components/LoginForm'
import LoginMFAForm from 'features/Login/components/LoginMFAForm'
import useCountdownTimer, { mapTimeToLabel } from 'hooks/useCountdownTimer'
import { GTLoginResponse, GTVerifyMfaCodeResponse } from 'gql/types.generated'

import { CHANGE_USER_PASSWORD, VERIFY_MFA_CODE } from 'features/Login/mutations-legacy'
import { ILoginFormValues, ILoginMFAFormValues, IResetPasswordFormValues } from 'features/Login/types'
import jwtDecode from 'jwt-decode'
import ResetPasswordForm from 'features/Login/components/ResetPasswordForm'
import { obfuscateEmail } from 'utils/string'
import { GET_URLS } from 'features/App/queries-legacy'

const { RELEASE } = process.env

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  background-color: rgb(246, 245, 247);
  position: absolute;
  overflow: auto;
`

const Content = styled.div`
  padding: 30px 0;
`

const CardWrapper = styled.div`
  max-width: 500px;
  width: 100%;
  margin: 10% auto 0;
`

const Logo = styled.div`
  text-align: center;
  margin-bottom: 30px;

  img {
    width: 150px;
    height: auto;
  }
`

const VersionContainer = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  position: absolute;
  bottom: 5px;
  right: 10px;
`

const CardContent = styled(Card.Content)`
  position: relative;
`

const IS_LOGGED_IN = gql`
  query isLoggedIn {
    login @client {
      accessToken {
        value
      }
    }
  }
`

const checkLogin = ({ accessToken }: TSFixMe): boolean => {
  return !isEmpty(accessToken.value)
}

const LOGIN = gql`
  mutation Login($username: String!, $password: String!) {
    login(input: { username: $username, password: $password }) {
      accessToken
      refreshToken
      mfaRequired
      passwordExpired
    }
  }
`
const REFRESH_SESSION = gql`
  mutation RefreshSession($refreshToken: String!) {
    refreshSession(input: { refreshToken: $refreshToken }) {
      accessToken
      refreshToken
    }
  }
`
const SET_TOKENS = gql`
  mutation SetTokens($accessToken: String!, $refreshToken: String!) {
    setTokens(accessToken: $accessToken, refreshToken: $refreshToken) @client
  }
`

const messages = defineMessages({
  helmet: {
    id: 'login.helmet',
    defaultMessage: 'Login | Nexbus Admin'
  }
})
const THREE_MINUTES = 180000

const getUserEmailFromAccessToken = (accessToken: string): string => {
  const decodedToken = (jwtDecode(accessToken) as { eml: string }) || { eml: '' }
  return obfuscateEmail(decodedToken.eml)
}
const LoginView = (): ReactElement => {
  const navigate = useNavigate()
  const client = useApolloClient()
  client.addResolvers(loginResolvers)
  const intl = useIntl()
  const [userEmail, setUserEmail] = useState<string>('')
  const [isShowingMFA, setIsShowingMFA] = useState<boolean>(false)
  const [loginFormValues, setLoginFormValues] = useState<ILoginFormValues>({
    username: '',
    password: ''
  })
  const [isPasswordExpired, setIsPasswordExpired] = useState<boolean>(false)

  const { time, start } = useCountdownTimer(THREE_MINUTES)
  const {
    data: { login: currentLogin }
  } = useQuery(IS_LOGGED_IN)
  const {
    data: { urls }
  } = useQuery(GET_URLS)
  const isLoggedIn = checkLogin(currentLogin)

  const [setTokens] = useMutation(SET_TOKENS)
  const [temporallyLoginTokens, setTemporallyLoginTokens] = useState<{
    accessToken: string
    refreshToken: string
  }>({
    accessToken: '',
    refreshToken: ''
  })

  const [refreshSession, { loading: refreshLoading, error: refreshError }] = useMutation<{
    refreshSession: GTLoginResponse
  }>(REFRESH_SESSION, {
    context: {
      headers: {
        authorization: `Bearer ${temporallyLoginTokens.accessToken}`
      }
    }
  })

  const [changeUserPassword, { loading: changePasswordLoading, error: changePasswordError }] = useMutation<{
    changeUserPassword: GTLoginResponse
  }>(CHANGE_USER_PASSWORD, {
    context: {
      headers: {
        authorization: `Bearer ${temporallyLoginTokens.accessToken}`
      }
    }
  })

  const [verifyMfaCode, { loading: mfaLoading, error: mfaError, reset: resetMfa }] = useMutation<{
    verifyMfaCode: GTVerifyMfaCodeResponse
  }>(VERIFY_MFA_CODE, {
    context: {
      headers: {
        authorization: `Bearer ${temporallyLoginTokens.accessToken}`
      }
    }
  })

  const [login, { loading, error, data }] = useMutation<{ login: GTLoginResponse }>(LOGIN)

  const handleMFALogin = async (values: ILoginMFAFormValues): Promise<void> => {
    try {
      const res = await verifyMfaCode({
        variables: {
          code: {
            code: values.code.join('')
          }
        }
      })

      if (isPasswordExpired) {
        setTemporallyLoginTokens({
          accessToken: res.data?.verifyMfaCode.accessToken ?? '',
          refreshToken: res.data?.verifyMfaCode.refreshToken ?? ''
        })
        setIsShowingMFA(false)
        return
      }

      setTokensAndGoHome({
        accessToken: res.data?.verifyMfaCode.accessToken as string,
        refreshToken: res.data?.verifyMfaCode.refreshToken as string
      })
    } catch (e) {
      //Do nothing
    }
  }

  const handleResetPassword = async (values: IResetPasswordFormValues): Promise<void> => {
    try {
      await changeUserPassword({
        variables: {
          input: {
            password: values.password
          }
        }
      })
      const { data: refreshSessionData } = await refreshSession({
        variables: {
          refreshToken: temporallyLoginTokens.refreshToken
        }
      })
      await setTokensAndGoHome({
        accessToken: refreshSessionData?.refreshSession.accessToken as string,
        refreshToken: refreshSessionData?.refreshSession.refreshToken as string
      })
    } catch (e) {
      //Do nothing
    }
  }

  const setTokensAndGoHome = async ({
    accessToken,
    refreshToken
  }: {
    accessToken: string
    refreshToken: string
  }): Promise<void> => {
    await setTokens({
      variables: {
        accessToken,
        refreshToken
      }
    })
    navigate(urls.last)
  }

  const handleLogin = async (values: ILoginFormValues): Promise<void> => {
    try {
      const { data } = await login({
        variables: {
          username: values.username,
          password: values.password
        }
      })

      setTemporallyLoginTokens({
        accessToken: data?.login.accessToken ?? '',
        refreshToken: data?.login.refreshToken ?? ''
      })

      setIsPasswordExpired(data?.login.passwordExpired ?? false)

      if (data?.login.mfaRequired) {
        setUserEmail(getUserEmailFromAccessToken(data?.login.accessToken))
        setLoginFormValues(values)
        setIsShowingMFA(true)
        start()
        return
      }

      if (!data?.login.passwordExpired) {
        setTokensAndGoHome({
          accessToken: data?.login.accessToken as string,
          refreshToken: data?.login.refreshToken as string
        })
      }
    } catch (e) {
      //Do nothing
    }
  }

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (isLoggedIn) {
    return <Navigate to={urls.last} />
  }

  let content: ReactElement

  if (isShowingMFA) {
    content = (
      <LoginMFAForm
        userEmail={userEmail}
        timerLabel={mapTimeToLabel(time)}
        timerValue={time}
        hasCodeSent={!isEmpty(data)}
        reset={(): void => {
          if (mfaError) {
            resetMfa()
          }
        }}
        error={mfaError || error}
        loading={mfaLoading || loading}
        handleSubmit={handleMFALogin}
        handleResendCode={(): void => {
          if (mfaError) {
            resetMfa()
          }
          handleLogin(loginFormValues)
        }}
      />
    )
  } else if (isPasswordExpired) {
    content = (
      <ResetPasswordForm
        error={changePasswordError || refreshError}
        loading={changePasswordLoading || refreshLoading}
        handleSubmit={handleResetPassword}
      />
    )
  } else {
    content = <LoginForm error={error} loading={loading} handleSubmit={handleLogin} />
  }

  return (
    <Wrapper>
      <Content>
        <CardWrapper>
          <Card.Container>
            <CardContent>
              <Logo>
                <img src={logoKolors} />
              </Logo>
              {content}
              {RELEASE && <VersionContainer>V{RELEASE}</VersionContainer>}
            </CardContent>
          </Card.Container>
        </CardWrapper>
      </Content>
    </Wrapper>
  )
}

export default LoginView
